import { useState, useEffect, useRef } from "react";
import { db } from "../firebase/config";
import {
  doc, getDoc, setDoc, getDocs, collection, query, where,
  writeBatch, serverTimestamp,
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import bcrypt from "bcryptjs";

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────
const SOCIETY_FIELDS = [
  { key: "societyName", label: "Society Name",     type: "text"   },
  { key: "regdNo",      label: "Registration No.", type: "text"   },
  { key: "emailId",     label: "Email ID",         type: "email"  },
  { key: "location",    label: "Location",         type: "text"   },
  { key: "totalFlats",  label: "Total Flats",      type: "number" },
];

const FINANCIAL_FIELDS = [
  { key: "maintenanceAmt", label: "Maintenance (Occupied) ₹", type: "number" },
  { key: "waterDeposit",   label: "Water Corpus ₹",           type: "number" },
  { key: "dueCutoffDay",   label: "Due Cutoff Day (1–28)",    type: "number" },
];

const ALL_NUMBER_KEYS = [
  "totalFlats", "maintenanceAmt", "vacantAmt", "vacantPct", "waterDeposit", "dueCutoffDay",
  "maintOpeningBalance", "corpusFund",
];

const DESIGNATION_OPTIONS = ["Watchman", "Sweeper", "Gardener", "Other"];
const POLICE_STATUS_OPTIONS = ["Pending", "Verified", "Not Required"];

const INNER_TABS = [
  { id: "society",   label: "Society",   icon: "🏢" },
  { id: "financial", label: "Financial", icon: "💰" },
  { id: "staff",     label: "Staff",     icon: "🧑‍💼" },
];

// ─────────────────────────────────────────────────────────────
// Shared sub-components
// ─────────────────────────────────────────────────────────────
function FieldGroup({ fields, form, onChange }) {
  return (
    <>
      {fields.map(field => (
        <div key={field.key} style={{ marginBottom: "14px" }}>
          <label style={{ display: "block", fontSize: "12px", color: "#8888a8", marginBottom: "5px" }}>
            {field.label}
          </label>
          <input
            type={field.type === "number" ? "number" : field.type === "email" ? "email" : "text"}
            value={form[field.key] ?? ""}
            onChange={e => onChange(field.key, e.target.value)}
            style={inputStyle}
          />
        </div>
      ))}
    </>
  );
}

function SectionDivider({ label }) {
  return (
    <div style={{
      fontSize: "11px", fontWeight: "700", color: "#6c63ff",
      textTransform: "uppercase", letterSpacing: "1px",
      margin: "18px 0 12px", borderBottom: "1px solid #2e2e40", paddingBottom: "6px",
    }}>
      {label}
    </div>
  );
}

function CollapseSection({ title, expanded, onToggle, children }) {
  return (
    <div style={{ marginBottom: "8px" }}>
      <button
        onClick={onToggle}
        style={{
          width: "100%", background: "#13131a", border: "1px solid #2e2e40",
          borderRadius: "10px", padding: "11px 14px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          cursor: "pointer", color: "#e8e8f0", fontSize: "13px", fontWeight: "600",
        }}
      >
        <span>{title}</span>
        <span style={{ color: "#555570", fontSize: "12px" }}>{expanded ? "▲" : "▼"}</span>
      </button>
      {expanded && (
        <div style={{
          background: "#0f0f13", border: "1px solid #2e2e40", borderTop: "none",
          borderRadius: "0 0 10px 10px", padding: "14px",
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Photo upload helper — resize to ~200×200 JPEG via canvas
// ─────────────────────────────────────────────────────────────
function resizeImageToBase64(file, maxPx = 200) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(maxPx / img.width, maxPx / img.height, 1);
        canvas.width  = Math.round(img.width  * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function PhotoUpload({ label, value, onChange }) {
  const cameraRef  = useRef();
  const galleryRef = useRef();

  async function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const b64 = await resizeImageToBase64(file);
      onChange(b64);
    } catch {
      // silently ignore
    }
    e.target.value = "";
  }

  return (
    <div style={{ marginBottom: "14px" }}>
      <label style={{ display: "block", fontSize: "12px", color: "#8888a8", marginBottom: "6px" }}>
        {label}
      </label>
      <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
        {/* Thumbnail */}
        {value ? (
          <img
            src={value}
            alt="photo"
            style={{ width: "60px", height: "60px", borderRadius: "50%", objectFit: "cover", border: "2px solid #6c63ff40", flexShrink: 0 }}
          />
        ) : (
          <div style={{
            width: "60px", height: "60px", borderRadius: "50%",
            background: "#1a1a24", border: "2px dashed #2e2e40",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "22px", flexShrink: 0,
          }}>👤</div>
        )}

        {/* Buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px", flex: 1 }}>
          <div style={{ display: "flex", gap: "6px" }}>
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              style={{
                flex: 1, background: "#6c63ff20", border: "1px solid #6c63ff40",
                borderRadius: "8px", padding: "7px 8px",
                fontSize: "12px", color: "#6c63ff", cursor: "pointer",
              }}
            >
              📷 Camera
            </button>
            <button
              type="button"
              onClick={() => galleryRef.current?.click()}
              style={{
                flex: 1, background: "#42a5f510", border: "1px solid #42a5f530",
                borderRadius: "8px", padding: "7px 8px",
                fontSize: "12px", color: "#42a5f5", cursor: "pointer",
              }}
            >
              🖼️ Gallery
            </button>
          </div>
          {value && (
            <button
              type="button"
              onClick={() => onChange(null)}
              style={{
                background: "#ff525210", border: "1px solid #ff525230",
                borderRadius: "8px", padding: "6px 12px",
                fontSize: "12px", color: "#ff5252", cursor: "pointer",
              }}
            >
              Remove
            </button>
          )}
        </div>
      </div>

      {/* Camera — opens device camera directly */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={handleFile}
      />
      {/* Gallery — opens photo picker */}
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFile}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Staff Card — individual active staff member
// ─────────────────────────────────────────────────────────────
function StaffCard({ staffDoc, userId, onSaved, onExited }) {
  const { user: currentUser } = useAuth();

  // ── form state
  const [form, setForm]       = useState({ ...staffDoc });
  const [saving, setSaving]   = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [saveErr, setSaveErr] = useState("");

  // ── section expand state
  const [sec1Open, setSec1Open] = useState(false);
  const [sec2Open, setSec2Open] = useState(false);

  // ── PIN reset state
  const [newPin, setNewPin]     = useState("");
  const [pinSaving, setPinSaving] = useState(false);
  const [pinMsg, setPinMsg]     = useState("");
  const [pinErr, setPinErr]     = useState("");

  // ── offboarding
  const [exitConfirm, setExitConfirm] = useState(false);
  const [exiting, setExiting]         = useState(false);
  const [exitErr, setExitErr]         = useState("");

  function setField(key, value) {
    setSaveMsg(""); setSaveErr("");
    setForm(prev => ({ ...prev, [key]: value }));
  }
  function setSpouseField(key, value) {
    setSaveMsg(""); setSaveErr("");
    setForm(prev => ({ ...prev, spouse: { ...(prev.spouse || {}), [key]: value } }));
  }

  async function handleSave() {
    if (!form.name?.trim())        { setSaveErr("Name is required."); return; }
    if (!form.designation?.trim()) { setSaveErr("Designation is required."); return; }
    if (!form.mobile?.trim())      { setSaveErr("Mobile is required."); return; }
    if (!form.aadharNumber?.trim()) { setSaveErr("Aadhaar number is required."); return; }
    if (!form.dateOfJoining)       { setSaveErr("Date of joining is required."); return; }

    setSaving(true); setSaveErr("");
    try {
      const toSave = {
        ...form,
        updatedBy: currentUser.name,
        updatedAt: new Date().toISOString(),
      };

      // Save staffDetails doc (doc ID = userId)
      await setDoc(doc(db, "staffDetails", userId), toSave, { merge: true });

      // Auto-sync to contacts: upsert by staffUserId field
      const contactsQ = query(
        collection(db, "contacts"),
        where("staffUserId", "==", userId)
      );
      const contactsSnap = await getDocs(contactsQ);
      const contactPayload = {
        name:        form.name.trim(),
        phone:       form.mobile.trim(),
        category:    form.designation || "Staff",
        staffUserId: userId,
        updatedAt:   new Date().toISOString(),
      };
      if (contactsSnap.empty) {
        // create new contact
        const newRef = doc(collection(db, "contacts"));
        await setDoc(newRef, { ...contactPayload, addedBy: currentUser.name, addedAt: new Date().toISOString() });
      } else {
        await setDoc(contactsSnap.docs[0].ref, contactPayload, { merge: true });
      }

      setSaveMsg("✅ Saved successfully.");
      setTimeout(() => setSaveMsg(""), 3000);
      onSaved?.();
    } catch (e) {
      setSaveErr("Save failed. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handlePinReset() {
    setPinErr(""); setPinMsg("");
    if (!/^\d{4}$/.test(newPin)) { setPinErr("Enter a valid 4-digit PIN."); return; }
    setPinSaving(true);
    try {
      const hashed = await bcrypt.hash(newPin, 10);
      await setDoc(doc(db, "users", userId), {
        password:       hashed,
        failedAttempts: 0,
        lockedUntil:    null,
        pinResetBy:     currentUser.name,
        pinResetAt:     new Date().toISOString(),
      }, { merge: true });
      setNewPin("");
      setPinMsg("✅ PIN updated.");
      setTimeout(() => setPinMsg(""), 3000);
    } catch {
      setPinErr("Failed to update PIN. Try again.");
    } finally {
      setPinSaving(false);
    }
  }

  async function handleMarkExited() {
    setExiting(true); setExitErr("");
    try {
      const batch = writeBatch(db);
      const today = new Date().toISOString().split("T")[0];

      // 1. staffDetails — set dateOfExit
      batch.set(doc(db, "staffDetails", userId), { dateOfExit: today }, { merge: true });

      // 2. users — set active = false
      batch.set(doc(db, "users", userId), { active: false }, { merge: true });

      // 3. contacts — remove staff entry
      const contactsQ = query(
        collection(db, "contacts"),
        where("staffUserId", "==", userId)
      );
      const contactsSnap = await getDocs(contactsQ);
      contactsSnap.docs.forEach(d => batch.delete(d.ref));

      await batch.commit();
      onExited?.(userId, today);
    } catch {
      setExitErr("Offboarding failed. Please try again.");
      setExiting(false);
    }
  }

  const isLoginRole = ["Watchman"].includes(form.designation);

  return (
    <div style={{
      background: "#1a1a24", border: "1px solid #2e2e40",
      borderRadius: "14px", marginBottom: "12px", overflow: "hidden",
    }}>
      {/* Card header */}
      <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: "12px" }}>
        {form.photo ? (
          <img src={form.photo} alt={form.name}
            style={{ width: "46px", height: "46px", borderRadius: "50%", objectFit: "cover", border: "2px solid #6c63ff40", flexShrink: 0 }} />
        ) : (
          <div style={{
            width: "46px", height: "46px", borderRadius: "50%",
            background: "#0f0f13", border: "2px dashed #2e2e40",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "20px", flexShrink: 0,
          }}>👤</div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>{form.name || "—"}</div>
          <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>
            {form.designation || "—"} · {form.mobile || "—"}
          </div>
          {form.dateOfJoining && (
            <div style={{ fontSize: "10px", color: "#555570", marginTop: "2px" }}>
              Since {form.dateOfJoining}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: "6px" }}>
          <button
            onClick={() => { setSec1Open(v => !v); if (sec2Open) setSec2Open(false); }}
            style={{
              background: sec1Open ? "#6c63ff20" : "#13131a",
              border: `1px solid ${sec1Open ? "#6c63ff40" : "#2e2e40"}`,
              borderRadius: "8px", padding: "6px 10px",
              fontSize: "11px", color: sec1Open ? "#6c63ff" : "#8888a8", cursor: "pointer",
            }}
          >
            Edit
          </button>
        </div>
      </div>

      {/* Section 1 — Essential */}
      {sec1Open && (
        <div style={{ padding: "0 14px 14px" }}>
          <CollapseSection
            title="📋 Essential Details"
            expanded={true}
            onToggle={() => {}}
          >
            <SectionDivider label="Staff" />

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Name *</label>
              <input value={form.name || ""} onChange={e => setField("name", e.target.value)} style={inputStyle} />
            </div>

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Designation *</label>
              <select
                value={form.designation || ""}
                onChange={e => setField("designation", e.target.value)}
                style={{ ...inputStyle, appearance: "none" }}
              >
                <option value="">Select…</option>
                {DESIGNATION_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Mobile * (login ID)</label>
              <input
                type="tel" inputMode="numeric" maxLength={10}
                value={form.mobile || ""}
                onChange={e => setField("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Aadhaar Number *</label>
              <input
                type="tel" inputMode="numeric" maxLength={12}
                value={form.aadharNumber || ""}
                onChange={e => setField("aadharNumber", e.target.value.replace(/\D/g, "").slice(0, 12))}
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Date of Joining *</label>
              <input
                type="date"
                value={form.dateOfJoining || ""}
                onChange={e => setField("dateOfJoining", e.target.value)}
                style={inputStyle}
              />
            </div>

            <PhotoUpload
              label="Staff Photo"
              value={form.photo || null}
              onChange={v => setField("photo", v)}
            />

            <SectionDivider label="Spouse / Family Member" />

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Spouse Name</label>
              <input
                value={form.spouse?.name || ""}
                onChange={e => setSpouseField("name", e.target.value)}
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Spouse Aadhaar</label>
              <input
                type="tel" inputMode="numeric" maxLength={12}
                value={form.spouse?.aadharNumber || ""}
                onChange={e => setSpouseField("aadharNumber", e.target.value.replace(/\D/g, "").slice(0, 12))}
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Spouse Mobile</label>
              <input
                type="tel" inputMode="numeric" maxLength={10}
                value={form.spouse?.mobile || ""}
                onChange={e => setSpouseField("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
                style={inputStyle}
              />
            </div>

            <PhotoUpload
              label="Spouse Photo"
              value={form.spouse?.photo || null}
              onChange={v => setSpouseField("photo", v)}
            />
          </CollapseSection>

          <CollapseSection
            title="📁 Additional Details"
            expanded={sec2Open}
            onToggle={() => setSec2Open(v => !v)}
          >
            <SectionDivider label="Contact & Address" />

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Alt Contact (Permanent address number)</label>
              <input
                type="tel" inputMode="numeric" maxLength={10}
                value={form.altContact || ""}
                onChange={e => setField("altContact", e.target.value.replace(/\D/g, "").slice(0, 10))}
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Permanent Address</label>
              <textarea
                value={form.permanentAddress || ""}
                onChange={e => setField("permanentAddress", e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </div>

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Local Address</label>
              <textarea
                value={form.localAddress || ""}
                onChange={e => setField("localAddress", e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </div>

            <SectionDivider label="Verification & Employment" />

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Police Verification Status</label>
              <select
                value={form.policeVerification?.status || ""}
                onChange={e => setField("policeVerification", {
                  ...(form.policeVerification || {}), status: e.target.value,
                })}
                style={{ ...inputStyle, appearance: "none" }}
              >
                <option value="">Select…</option>
                {POLICE_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {form.policeVerification?.status === "Verified" && (
              <div style={{ marginBottom: "14px" }}>
                <label style={labelStyle}>Verification Date</label>
                <input
                  type="date"
                  value={form.policeVerification?.date || ""}
                  onChange={e => setField("policeVerification", {
                    ...(form.policeVerification || {}), date: e.target.value,
                  })}
                  style={inputStyle}
                />
              </div>
            )}

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Salary Terms</label>
              <textarea
                value={form.salaryTerms || ""}
                onChange={e => setField("salaryTerms", e.target.value)}
                rows={2}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </div>

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Notes</label>
              <textarea
                value={form.notes || ""}
                onChange={e => setField("notes", e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </div>

            <SectionDivider label="Spouse Details" />

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Spouse Permanent Address</label>
              <textarea
                value={form.spouse?.permanentAddress || ""}
                onChange={e => setSpouseField("permanentAddress", e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </div>

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Spouse Local Address</label>
              <textarea
                value={form.spouse?.localAddress || ""}
                onChange={e => setSpouseField("localAddress", e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </div>

            <div style={{ marginBottom: "14px" }}>
              <label style={labelStyle}>Spouse Police Verification Status</label>
              <select
                value={form.spouse?.policeVerification?.status || ""}
                onChange={e => setSpouseField("policeVerification", {
                  ...(form.spouse?.policeVerification || {}), status: e.target.value,
                })}
                style={{ ...inputStyle, appearance: "none" }}
              >
                <option value="">Select…</option>
                {POLICE_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </CollapseSection>

          {/* PIN Reset — only for login-enabled designations */}
          {isLoginRole && (
            <div style={{
              background: "#13131a", border: "1px solid #2e2e40",
              borderRadius: "10px", padding: "14px", marginTop: "8px",
            }}>
              <div style={{ fontSize: "12px", fontWeight: "600", color: "#8888a8", marginBottom: "10px" }}>
                🔑 Reset Login PIN
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <input
                  type="tel" inputMode="numeric" maxLength={4}
                  placeholder="New 4-digit PIN"
                  value={newPin}
                  onChange={e => {
                    setPinErr(""); setPinMsg("");
                    setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4));
                  }}
                  style={{
                    ...inputStyle, flex: 1, letterSpacing: "6px",
                    textAlign: "center", fontSize: "18px",
                  }}
                />
                <button
                  onClick={handlePinReset}
                  disabled={pinSaving}
                  style={{
                    background: "#6c63ff", color: "#fff", border: "none",
                    borderRadius: "10px", padding: "12px 16px",
                    fontSize: "13px", fontWeight: "600",
                    cursor: pinSaving ? "not-allowed" : "pointer",
                    opacity: pinSaving ? 0.7 : 1, whiteSpace: "nowrap",
                  }}
                >
                  {pinSaving ? "…" : "Set PIN"}
                </button>
              </div>
              {pinErr && <div style={{ fontSize: "12px", color: "#ff5252", marginTop: "6px" }}>⚠ {pinErr}</div>}
              {pinMsg && <div style={{ fontSize: "12px", color: "#4caf50", marginTop: "6px" }}>{pinMsg}</div>}
            </div>
          )}

          {/* Messages */}
          {saveErr && (
            <div style={{
              background: "#ff525215", border: "1px solid #ff525240", borderRadius: "10px",
              padding: "10px 14px", fontSize: "13px", color: "#ff5252", marginTop: "10px",
            }}>⚠ {saveErr}</div>
          )}
          {saveMsg && (
            <div style={{
              background: "#4caf5015", border: "1px solid #4caf5040", borderRadius: "10px",
              padding: "10px 14px", fontSize: "13px", color: "#4caf50", marginTop: "10px",
            }}>{saveMsg}</div>
          )}

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              width: "100%", background: "#6c63ff", color: "#fff",
              border: "none", borderRadius: "10px", padding: "13px",
              fontSize: "14px", fontWeight: "600", marginTop: "12px",
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? "Saving…" : "Save"}
          </button>

          {/* Mark as Exited */}
          <div style={{ marginTop: "16px", borderTop: "1px solid #2e2e40", paddingTop: "14px" }}>
            {!exitConfirm ? (
              <button
                onClick={() => setExitConfirm(true)}
                style={{
                  width: "100%", background: "#ff525210", color: "#ff5252",
                  border: "1px solid #ff525240", borderRadius: "10px",
                  padding: "11px", fontSize: "13px", fontWeight: "600", cursor: "pointer",
                }}
              >
                Mark as Exited
              </button>
            ) : (
              <div style={{
                background: "#ff525210", border: "1px solid #ff525240",
                borderRadius: "10px", padding: "14px",
              }}>
                <div style={{ fontSize: "13px", color: "#ff5252", marginBottom: "10px", fontWeight: "600" }}>
                  Are you sure? This will disable {form.name}'s login immediately.
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => setExitConfirm(false)}
                    style={{
                      flex: 1, background: "#13131a", color: "#8888a8",
                      border: "1px solid #2e2e40", borderRadius: "8px",
                      padding: "10px", fontSize: "13px", cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleMarkExited}
                    disabled={exiting}
                    style={{
                      flex: 1, background: "#ff5252", color: "#fff",
                      border: "none", borderRadius: "8px",
                      padding: "10px", fontSize: "13px", fontWeight: "700",
                      cursor: exiting ? "not-allowed" : "pointer",
                      opacity: exiting ? 0.7 : 1,
                    }}
                  >
                    {exiting ? "Processing…" : "Confirm Exit"}
                  </button>
                </div>
                {exitErr && (
                  <div style={{ fontSize: "12px", color: "#ff5252", marginTop: "8px" }}>{exitErr}</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Add Staff Form
// ─────────────────────────────────────────────────────────────
function AddStaffForm({ onAdded, onCancel }) {
  const { user: currentUser } = useAuth();
  const [form, setForm] = useState({
    name: "", designation: "", mobile: "", aadharNumber: "", dateOfJoining: "",
    photo: null,
    spouse: { name: "", aadharNumber: "", mobile: "", photo: null },
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState("");
  const [generatedPin, setGeneratedPin] = useState(null);
  const [addedName, setAddedName]       = useState("");

  function setField(key, value) { setErr(""); setForm(prev => ({ ...prev, [key]: value })); }
  function setSpouseField(key, value) {
    setErr("");
    setForm(prev => ({ ...prev, spouse: { ...(prev.spouse || {}), [key]: value } }));
  }

  async function handleAdd() {
    if (!form.name?.trim())         { setErr("Name is required."); return; }
    if (!form.designation)          { setErr("Designation is required."); return; }
    if (!/^\d{10}$/.test(form.mobile)) { setErr("Enter a valid 10-digit mobile."); return; }
    if (!form.aadharNumber?.trim()) { setErr("Aadhaar number is required."); return; }
    if (!form.dateOfJoining)        { setErr("Date of joining is required."); return; }

    setSaving(true);
    try {
      // Generate a random 4-digit starting PIN — shown once on screen below,
      // never stored in plain text (hashed the same way Reset PIN does it).
      const startingPin = String(Math.floor(1000 + Math.random() * 9000));
      const hashedPin    = await bcrypt.hash(startingPin, 10);

      // Create users doc first (auto-ID)
      const { addDoc, collection: col } = await import("firebase/firestore");
      const userRef = await addDoc(col(db, "users"), {
        name:           form.name.trim(),
        mobile:         form.mobile.trim(),
        role:           form.designation === "Watchman" ? "Watchman" : "Staff",
        flatId:         "",
        memberType:     "staff",
        password:       hashedPin,  // random per-staff starting PIN, not a shared default
        failedAttempts: 0,
        active:         true,
        createdBy:      currentUser.name,
        createdAt:      new Date().toISOString(),
      });

      const userId = userRef.id;

      // Create staffDetails doc (same ID as users doc)
      await setDoc(doc(db, "staffDetails", userId), {
        ...form,
        active:    true,
        createdBy: currentUser.name,
        createdAt: new Date().toISOString(),
      });

      // Auto-sync to contacts
      const newRef = doc(collection(db, "contacts"));
      await setDoc(newRef, {
        name:        form.name.trim(),
        phone:       form.mobile.trim(),
        category:    form.designation,
        staffUserId: userId,
        addedBy:     currentUser.name,
        addedAt:     new Date().toISOString(),
      });

      // Show the PIN once instead of closing straight away — MC needs to
      // note it down and hand it to the staff member.
      setAddedName(form.name.trim());
      setGeneratedPin(startingPin);
      setSaving(false);
    } catch (e) {
      setErr("Failed to add staff. Try again.");
      setSaving(false);
    }
  }

  // Success screen — shown once right after a staff member is added,
  // so the MC can note down the PIN before it's ever asked for again.
  if (generatedPin) {
    return (
      <div style={{
        background: "#1a1a24", border: "1px solid #6c63ff40",
        borderRadius: "14px", padding: "16px", marginBottom: "16px",
      }}>
        <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0", marginBottom: "14px" }}>
          ✅ Staff member added
        </div>
        <div style={{
          background: "#4caf5015", border: "1px solid #4caf5040", borderRadius: "10px",
          padding: "14px 16px", marginBottom: "16px",
        }}>
          <div style={{ fontSize: "13px", color: "#8888a8", marginBottom: "8px" }}>
            New login PIN for {addedName}
          </div>
          <div style={{
            fontSize: "28px", fontWeight: "700", letterSpacing: "6px",
            color: "#e8e8f0", marginBottom: "10px",
          }}>
            {generatedPin}
          </div>
          <div style={{ fontSize: "11px", color: "#555570" }}>
            Note this down and share it with them directly. It won't be shown again — use "Reset Login PIN" on their card later if it ever needs to change.
          </div>
        </div>
        <button
          onClick={onAdded}
          style={{
            width: "100%", background: "#6c63ff", color: "#fff",
            border: "none", borderRadius: "10px", padding: "13px",
            fontSize: "15px", fontWeight: "600", cursor: "pointer",
          }}
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <div style={{
      background: "#1a1a24", border: "1px solid #6c63ff40",
      borderRadius: "14px", padding: "16px", marginBottom: "16px",
    }}>
      <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0", marginBottom: "14px" }}>
        ➕ Add New Staff Member
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>Name *</label>
        <input value={form.name} onChange={e => setField("name", e.target.value)} style={inputStyle} />
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>Designation *</label>
        <select value={form.designation} onChange={e => setField("designation", e.target.value)} style={{ ...inputStyle, appearance: "none" }}>
          <option value="">Select…</option>
          {DESIGNATION_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>Mobile * (will be login ID)</label>
        <input
          type="tel" inputMode="numeric" maxLength={10}
          value={form.mobile}
          onChange={e => setField("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>Aadhaar Number *</label>
        <input
          type="tel" inputMode="numeric" maxLength={12}
          value={form.aadharNumber}
          onChange={e => setField("aadharNumber", e.target.value.replace(/\D/g, "").slice(0, 12))}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>Date of Joining *</label>
        <input type="date" value={form.dateOfJoining} onChange={e => setField("dateOfJoining", e.target.value)} style={inputStyle} />
      </div>

      <PhotoUpload label="Staff Photo" value={form.photo} onChange={v => setField("photo", v)} />

      <SectionDivider label="Spouse" />

      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>Spouse Name</label>
        <input value={form.spouse.name} onChange={e => setSpouseField("name", e.target.value)} style={inputStyle} />
      </div>
      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>Spouse Aadhaar</label>
        <input
          type="tel" inputMode="numeric" maxLength={12}
          value={form.spouse.aadharNumber}
          onChange={e => setSpouseField("aadharNumber", e.target.value.replace(/\D/g, "").slice(0, 12))}
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>Spouse Mobile</label>
        <input
          type="tel" inputMode="numeric" maxLength={10}
          value={form.spouse.mobile}
          onChange={e => setSpouseField("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
          style={inputStyle}
        />
      </div>
      <PhotoUpload label="Spouse Photo" value={form.spouse.photo} onChange={v => setSpouseField("photo", v)} />

      {err && (
        <div style={{
          background: "#ff525215", border: "1px solid #ff525240", borderRadius: "10px",
          padding: "10px 14px", fontSize: "13px", color: "#ff5252", marginBottom: "12px",
        }}>⚠ {err}</div>
      )}

      <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
        <button
          onClick={onCancel}
          style={{
            flex: 1, background: "#13131a", color: "#8888a8",
            border: "1px solid #2e2e40", borderRadius: "10px",
            padding: "12px", fontSize: "14px", cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleAdd}
          disabled={saving}
          style={{
            flex: 2, background: "#6c63ff", color: "#fff",
            border: "none", borderRadius: "10px", padding: "12px",
            fontSize: "14px", fontWeight: "600",
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? "Adding…" : "Add Staff Member"}
        </button>
      </div>

      <div style={{ fontSize: "11px", color: "#555570", marginTop: "10px", textAlign: "center" }}>
        A random login PIN will be generated and shown once after adding.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Past Staff Card — with Reactivate confirmation
// ─────────────────────────────────────────────────────────────
function PastStaffCard({ s, onReactivate }) {
  const [confirm, setConfirm]   = useState(false);
  const [loading, setLoading]   = useState(false);

  async function handleConfirm() {
    setLoading(true);
    await onReactivate(s.userId);
    setLoading(false);
    setConfirm(false);
  }

  return (
    <div style={{
      background: "#13131a", border: "1px solid #2e2e40", borderRadius: "12px",
      padding: "12px 14px", marginBottom: "8px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", opacity: 0.7 }}>
        {s.photo ? (
          <img src={s.photo} alt={s.name}
            style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
        ) : (
          <div style={{
            width: "40px", height: "40px", borderRadius: "50%",
            background: "#0f0f13", border: "1px solid #2e2e40",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0,
          }}>👤</div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "13px", fontWeight: "600", color: "#8888a8" }}>{s.name}</div>
          <div style={{ fontSize: "11px", color: "#555570" }}>{s.designation} · {s.mobile}</div>
        </div>
        <div style={{
          background: "#ff525215", border: "1px solid #ff525230",
          borderRadius: "6px", padding: "3px 8px", fontSize: "10px", color: "#ff5252", fontWeight: "600",
        }}>
          Exited {s.dateOfExit}
        </div>
      </div>
      {!confirm ? (
        <button
          onClick={() => setConfirm(true)}
          style={{
            width: "100%", marginTop: "10px",
            background: "#4caf5010", color: "#4caf50",
            border: "1px solid #4caf5040", borderRadius: "8px",
            padding: "8px", fontSize: "12px", fontWeight: "600", cursor: "pointer",
          }}
        >
          ↩ Reactivate
        </button>
      ) : (
        <div style={{
          marginTop: "10px", background: "#4caf5010", border: "1px solid #4caf5040",
          borderRadius: "8px", padding: "10px",
        }}>
          <div style={{ fontSize: "12px", color: "#4caf50", marginBottom: "8px", fontWeight: "600" }}>
            Reactivate {s.name}? This will restore their login and contacts entry.
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => setConfirm(false)}
              style={{
                flex: 1, background: "#13131a", color: "#8888a8",
                border: "1px solid #2e2e40", borderRadius: "8px",
                padding: "8px", fontSize: "12px", cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading}
              style={{
                flex: 1, background: "#4caf50", color: "#fff",
                border: "none", borderRadius: "8px",
                padding: "8px", fontSize: "12px", fontWeight: "700",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? "…" : "Confirm"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Staff Tab
// ─────────────────────────────────────────────────────────────
function StaffTab() {
  const [staffList, setStaffList]       = useState([]);  // { userId, ...staffDetails }
  const [loading, setLoading]           = useState(true);
  const [showAddForm, setShowAddForm]   = useState(false);
  const [pastExpanded, setPastExpanded] = useState(false);

  async function loadStaff() {
    setLoading(true);
    try {
      // 1. Fetch all existing staffDetails docs
      const staffSnap = await getDocs(collection(db, "staffDetails"));
      const existingIds = new Set(staffSnap.docs.map(d => d.id));

      // 2. Find any users with role:Watchman or memberType:staff that have no staffDetails doc yet
      const [watchmanSnap, staffMemberSnap] = await Promise.all([
        getDocs(query(collection(db, "users"), where("role", "==", "Watchman"))),
        getDocs(query(collection(db, "users"), where("memberType", "==", "staff"))),
      ]);

      // Merge, deduplicate by doc ID
      const userDocsMap = new Map();
      [...watchmanSnap.docs, ...staffMemberSnap.docs].forEach(d => {
        if (!userDocsMap.has(d.id)) userDocsMap.set(d.id, d);
      });

      // 3. Auto-create minimal staffDetails stub for any missing ones
      const stubPromises = [];
      userDocsMap.forEach((userDoc, userId) => {
        if (!existingIds.has(userId)) {
          const u = userDoc.data();
          const stub = {
            name:        u.name || "",
            mobile:      u.mobile || "",
            designation: u.role === "Watchman" ? "Watchman" : (u.designation || "Other"),
            active:      u.active !== false,
            createdAt:   u.createdAt || new Date().toISOString(),
            stubAutoCreated: true,  // flag so MC knows to fill in full details
          };
          stubPromises.push(
            setDoc(doc(db, "staffDetails", userId), stub, { merge: true })
          );
          // Add to existingIds so it shows up in the list below
          existingIds.add(userId);
          staffSnap.docs.push({ id: userId, data: () => stub });
        }
      });
      if (stubPromises.length > 0) await Promise.all(stubPromises);

      // 4. Build final list from staffDetails (now includes any new stubs)
      const allStaffDocs = await getDocs(collection(db, "staffDetails"));
      setStaffList(allStaffDocs.docs.map(d => ({ userId: d.id, ...d.data() })));
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadStaff(); }, []);

  function handleExited(userId, exitDate) {
    setStaffList(prev =>
      prev.map(s => s.userId === userId ? { ...s, dateOfExit: exitDate } : s)
    );
  }

  async function handleReactivate(userId) {
    try {
      const staffMember = staffList.find(s => s.userId === userId);

      // 1. staffDetails — clear dateOfExit
      await setDoc(doc(db, "staffDetails", userId), { dateOfExit: null }, { merge: true });

      // 2. users — set active = true
      await setDoc(doc(db, "users", userId), { active: true }, { merge: true });

      // 3. contacts — re-add if not already there
      if (staffMember) {
        const contactsQ = query(collection(db, "contacts"), where("staffUserId", "==", userId));
        const contactsSnap = await getDocs(contactsQ);
        if (contactsSnap.empty) {
          const newRef = doc(collection(db, "contacts"));
          await setDoc(newRef, {
            name:        staffMember.name || "",
            phone:       staffMember.mobile || "",
            category:    staffMember.designation || "Staff",
            staffUserId: userId,
            addedBy:     "reactivated",
            addedAt:     new Date().toISOString(),
          });
        }
      }

      // 4. Update local state
      setStaffList(prev =>
        prev.map(s => s.userId === userId ? { ...s, dateOfExit: null } : s)
      );
    } catch {
      // silently fail — MC can retry
    }
  }

  const activeStaff = staffList.filter(s => !s.dateOfExit);
  const pastStaff   = staffList.filter(s =>  s.dateOfExit);

  if (loading) {
    return <div style={{ textAlign: "center", padding: "40px", color: "#555570", fontSize: "13px" }}>Loading staff…</div>;
  }

  return (
    <div>
      <div style={{ fontSize: "12px", color: "#8888a8", marginBottom: "16px", lineHeight: "1.6" }}>
        Manage society staff — Watchman, Sweeper, Gardener, and others.
        All MC members (President, Secretary, Treasurer) can view and edit.
      </div>

      {/* Active staff */}
      <div style={{ fontSize: "12px", fontWeight: "700", color: "#6c63ff", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "10px" }}>
        Active Staff ({activeStaff.length})
      </div>

      {activeStaff.length === 0 && !showAddForm && (
        <div style={{
          background: "#1a1a24", border: "1px dashed #2e2e40", borderRadius: "12px",
          padding: "24px", textAlign: "center", marginBottom: "16px",
        }}>
          <div style={{ fontSize: "24px", marginBottom: "6px" }}>👤</div>
          <div style={{ fontSize: "13px", color: "#555570" }}>No active staff yet. Add one below.</div>
        </div>
      )}

      {activeStaff.map(s => (
        <StaffCard
          key={s.userId}
          staffDoc={s}
          userId={s.userId}
          onSaved={loadStaff}
          onExited={handleExited}
        />
      ))}

      {/* Add staff button / form */}
      {!showAddForm ? (
        <button
          onClick={() => setShowAddForm(true)}
          style={{
            width: "100%", background: "#6c63ff10", color: "#6c63ff",
            border: "1px dashed #6c63ff40", borderRadius: "12px",
            padding: "13px", fontSize: "14px", fontWeight: "600",
            cursor: "pointer", marginBottom: "16px",
          }}
        >
          ➕ Add Staff Member
        </button>
      ) : (
        <AddStaffForm
          onAdded={() => { setShowAddForm(false); loadStaff(); }}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {/* Past staff */}
      {pastStaff.length > 0 && (
        <>
          <button
            onClick={() => setPastExpanded(v => !v)}
            style={{
              width: "100%", background: "#13131a", border: "1px solid #2e2e40",
              borderRadius: "10px", padding: "11px 14px",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              cursor: "pointer", color: "#555570", fontSize: "13px", marginBottom: "8px",
            }}
          >
            <span>Past Staff ({pastStaff.length})</span>
            <span style={{ fontSize: "12px" }}>{pastExpanded ? "▲" : "▼"}</span>
          </button>

          {pastExpanded && pastStaff.map(s => (
            <PastStaffCard key={s.userId} s={s} onReactivate={handleReactivate} />
          ))}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Shared styles
// ─────────────────────────────────────────────────────────────
const inputStyle = {
  width: "100%", background: "#0f0f13",
  border: "1px solid #2e2e40", borderRadius: "10px",
  padding: "11px 14px", color: "#e8e8f0",
  fontSize: "14px", outline: "none", boxSizing: "border-box",
};
const labelStyle = {
  display: "block", fontSize: "12px", color: "#8888a8", marginBottom: "5px",
};

// ─────────────────────────────────────────────────────────────
// Main SettingsScreen
// ─────────────────────────────────────────────────────────────
export default function SettingsScreen() {
  const { isPresident } = useAuth();
  const [form,     setForm]     = useState({});
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [error,    setError]    = useState("");
  const [innerTab, setInnerTab] = useState(isPresident() ? "society" : "staff");

  const visibleInnerTabs = isPresident()
    ? INNER_TABS
    : INNER_TABS.filter(t => t.id === "staff");

  // FIX — Settings 4.1 (design frozen 2026-07-13): the Logo and Letterhead
  // images now live in their OWN Firestore documents (settings/logo and
  // settings/letterhead), separate from settings/init. Reason: settings/init
  // is read by nearly every screen in the app on load — Flats, Water,
  // Dashboard, etc. — so keeping large image blobs in there meant every
  // screen was silently downloading images it never uses. Splitting them
  // out means only the screens that actually need an image (Settings itself,
  // and wherever Logo/Letterhead get displayed/used) ever fetch it.
  useEffect(() => {
    async function load() {
      try {
        const [initSnap, logoSnap, letterheadSnap] = await Promise.all([
          getDoc(doc(db, "settings", "init")),
          getDoc(doc(db, "settings", "logo")),
          getDoc(doc(db, "settings", "letterhead")),
        ]);
        const data = initSnap.exists() ? initSnap.data() : {};
        if (data.vacantPct === undefined) data.vacantPct = 50;
        data.logoImage       = logoSnap.exists()       ? (logoSnap.data().image       || null) : null;
        data.letterheadImage = letterheadSnap.exists() ? (letterheadSnap.data().image || null) : null;
        setForm(data);
      } catch {
        setError("Failed to load settings.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  function handleChange(key, value) {
    setSaved(false); setError("");
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setSaving(true); setError(""); setSaved(false);
    try {
      const { logoImage, letterheadImage, ...rest } = form;
      const toSave = { ...rest };
      ALL_NUMBER_KEYS.forEach(k => {
        if (toSave[k] !== undefined && toSave[k] !== "") toSave[k] = Number(toSave[k]);
      });
      if (toSave.maintenanceAmt && toSave.vacantPct) {
        toSave.vacantAmt = Math.round(toSave.maintenanceAmt * toSave.vacantPct / 100);
      }
      // Three separate writes — regular settings, logo, and letterhead each
      // go to their own document. Firestore batches aren't needed here since
      // these are independent pieces of data with no all-or-nothing relationship.
      await Promise.all([
        setDoc(doc(db, "settings", "init"), toSave, { merge: true }),
        setDoc(doc(db, "settings", "logo"), { image: logoImage ?? null }, { merge: true }),
        setDoc(doc(db, "settings", "letterhead"), { image: letterheadImage ?? null }, { merge: true }),
      ]);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError("Save failed. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={{ textAlign: "center", padding: "40px", color: "#555570", fontSize: "13px" }}>Loading settings…</div>;
  }

  const maintenanceAmt = Number(form.maintenanceAmt) || 0;

  return (
    <div>
      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>⚙️ Society Settings</div>
        <div style={{ fontSize: "12px", color: "#555570", marginTop: "3px" }}>
          Changes save to Firestore and reflect everywhere instantly.
        </div>
      </div>

      {/* Inner tab bar */}
      <div style={{
        display: "flex", gap: "6px", marginBottom: "20px",
        background: "#1a1a24", borderRadius: "12px", padding: "4px",
      }}>
        {visibleInnerTabs.map(t => (
          <button
            key={t.id}
            onClick={() => setInnerTab(t.id)}
            style={{
              flex: 1, padding: "9px 6px", borderRadius: "9px", border: "none",
              background: innerTab === t.id ? "#6c63ff" : "transparent",
              color: innerTab === t.id ? "#fff" : "#8888a8",
              fontSize: "12px", fontWeight: "600", cursor: "pointer",
              transition: "background 0.15s",
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Society Tab */}
      {innerTab === "society" && (
        <>
          <FieldGroup fields={SOCIETY_FIELDS} form={form} onChange={handleChange} />

          {/* Full Address for report letterhead */}
          <div style={{ marginBottom: "14px" }}>
            <label style={labelStyle}>Full Address (for reports)</label>
            <textarea
              value={form.fullAddress ?? ""}
              onChange={e => handleChange("fullAddress", e.target.value)}
              rows={3}
              placeholder="e.g. Plot No. 12, Survey No. 45/2, Road No. 5, Lakeview Colony, City - 500001"
              style={{
                ...inputStyle, resize: "vertical", lineHeight: "1.5",
                fontFamily: "inherit", padding: "10px 14px",
              }}
            />
            <div style={{ fontSize: "11px", color: "#555570", marginTop: "4px" }}>
              Appears on PDF reports only — not shown in the app.
            </div>
          </div>

          {/* Society Logo (app) + Letterhead (reports) — side by side to save
              vertical space. Compact thumbnails and buttons that don't
              stretch full-width (fixed 2026-07-13 per UI feedback — the
              earlier stacked version had oversized buttons). */}
          <div style={{ display: "flex", gap: "16px", marginBottom: "14px", flexWrap: "wrap" }}>

            {/* Society Logo — shown IN THE APP (login screen, header, etc.),
                never on PDF reports. Saved separately to settings/logo. */}
            <div style={{ flex: "1 1 220px", minWidth: "200px" }}>
              <label style={labelStyle}>Society Logo</label>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                {form.logoImage ? (
                  <img src={form.logoImage} alt="Society logo"
                    style={{ width: "40px", height: "40px", borderRadius: "6px", objectFit: "contain", border: "1px solid #2e2e40", background: "#fff", flexShrink: 0 }} />
                ) : (
                  <div style={{
                    width: "40px", height: "40px", borderRadius: "6px",
                    background: "#1a1a24", border: "2px dashed #2e2e40",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "16px", flexShrink: 0,
                  }}>🏢</div>
                )}
                <input type="file" accept="image/*" style={{ display: "none" }} id="societylogo-upload"
                  onChange={async e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    try {
                      const b64 = await resizeImageToBase64(file, 300);
                      handleChange("logoImage", b64);
                    } catch {}
                    e.target.value = "";
                  }}
                />
                <button type="button" onClick={() => document.getElementById("societylogo-upload").click()}
                  style={{
                    background: "#6c63ff20", border: "1px solid #6c63ff40",
                    borderRadius: "7px", padding: "6px 10px",
                    fontSize: "11px", color: "#6c63ff", cursor: "pointer", whiteSpace: "nowrap",
                  }}>
                  Upload
                </button>
                {form.logoImage && (
                  <button type="button" onClick={() => handleChange("logoImage", null)}
                    style={{
                      background: "#ff525210", border: "1px solid #ff525230",
                      borderRadius: "7px", padding: "6px 10px",
                      fontSize: "11px", color: "#ff5252", cursor: "pointer", whiteSpace: "nowrap",
                    }}>
                    Remove
                  </button>
                )}
              </div>
              <div style={{ fontSize: "10px", color: "#555570", marginTop: "4px" }}>
                Shown in the app — not on PDF reports.
              </div>
            </div>

            {/* Letterhead image — used ONLY on PDF reports, never shown in the
                app itself. Saved separately to settings/letterhead. This was
                previously called "Society Logo" and lived in settings/init —
                renamed and relocated 2026-07-13 for clarity and to keep large
                images out of the document every screen reads on load. */}
            <div style={{ flex: "1 1 220px", minWidth: "200px" }}>
              <label style={labelStyle}>Letterhead (for reports)</label>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                {form.letterheadImage ? (
                  <img src={form.letterheadImage} alt="Letterhead"
                    style={{ width: "40px", height: "40px", borderRadius: "6px", objectFit: "contain", border: "1px solid #2e2e40", background: "#fff", flexShrink: 0 }} />
                ) : (
                  <div style={{
                    width: "40px", height: "40px", borderRadius: "6px",
                    background: "#1a1a24", border: "2px dashed #2e2e40",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "16px", flexShrink: 0,
                  }}>📄</div>
                )}
                <input type="file" accept="image/*" style={{ display: "none" }} id="letterhead-upload"
                  onChange={async e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    try {
                      const b64 = await resizeImageToBase64(file, 300);
                      handleChange("letterheadImage", b64);
                    } catch {}
                    e.target.value = "";
                  }}
                />
                <button type="button" onClick={() => document.getElementById("letterhead-upload").click()}
                  style={{
                    background: "#6c63ff20", border: "1px solid #6c63ff40",
                    borderRadius: "7px", padding: "6px 10px",
                    fontSize: "11px", color: "#6c63ff", cursor: "pointer", whiteSpace: "nowrap",
                  }}>
                  Upload
                </button>
                {form.letterheadImage && (
                  <button type="button" onClick={() => handleChange("letterheadImage", null)}
                    style={{
                      background: "#ff525210", border: "1px solid #ff525230",
                      borderRadius: "7px", padding: "6px 10px",
                      fontSize: "11px", color: "#ff5252", cursor: "pointer", whiteSpace: "nowrap",
                    }}>
                    Remove
                  </button>
                )}
              </div>
              <div style={{ fontSize: "10px", color: "#555570", marginTop: "4px" }}>
                Appears on PDF reports only — not shown in the app.
              </div>
            </div>

          </div>
        </>
      )}

      {/* Financial Tab */}
      {innerTab === "financial" && (
        <>
          {/* Row 1 — Maintenance (Occupied) + Opening Balance */}
          <div style={{ display: "flex", gap: "10px", marginBottom: "14px" }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Maintenance (Occupied) ₹</label>
              <input
                type="number"
                value={form.maintenanceAmt ?? ""}
                onChange={e => handleChange("maintenanceAmt", e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Opening Balance ₹</label>
              <input
                type="number"
                value={form.maintOpeningBalance ?? ""}
                onChange={e => handleChange("maintOpeningBalance", e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          {/* Row 2 — Water Corpus + Corpus Fund */}
          <div style={{ display: "flex", gap: "10px", marginBottom: "14px" }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Water Corpus ₹</label>
              <input
                type="number"
                value={form.waterDeposit ?? ""}
                onChange={e => handleChange("waterDeposit", e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Corpus Fund ₹</label>
              <input
                type="number"
                value={form.corpusFund ?? ""}
                onChange={e => handleChange("corpusFund", e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          {/* Row 3 — Due Cutoff Day (single, full width) */}
          <div style={{ marginBottom: "14px" }}>
            <label style={labelStyle}>Due Cutoff Day (1–28)</label>
            <input
              type="number" min="1" max="28"
              value={form.dueCutoffDay ?? ""}
              onChange={e => handleChange("dueCutoffDay", e.target.value)}
              style={{ ...inputStyle, maxWidth: "calc(50% - 5px)" }}
            />
          </div>

          {/* Row 4 — Vacant % + Computed */}
          <div style={{ marginBottom: "14px" }}>
            <label style={labelStyle}>Maintenance (Vacant) — % of Occupied</label>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <div style={{ position: "relative", width: "110px", flexShrink: 0 }}>
                <input
                  type="number" min="1" max="100"
                  value={form.vacantPct ?? 50}
                  onChange={e => handleChange("vacantPct", e.target.value)}
                  style={{ ...inputStyle, width: "100%", paddingRight: "30px" }}
                />
                <span style={{
                  position: "absolute", right: "12px", top: "50%",
                  transform: "translateY(-50%)", color: "#6c63ff", fontSize: "14px",
                  pointerEvents: "none",
                }}>%</span>
              </div>
              <div style={{
                flex: 1, background: "#0f0f13", border: "1px solid #2e2e40",
                borderRadius: "10px", padding: "11px 14px", color: "#6c63ff",
                fontSize: "14px", boxSizing: "border-box",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <span>
                  {maintenanceAmt > 0
                    ? `₹ ${Math.round(maintenanceAmt * (Number(form.vacantPct) || 50) / 100)}`
                    : "—"}
                </span>
                <span style={{ fontSize: "11px", color: "#555570" }}>computed</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Staff Tab */}
      {innerTab === "staff" && <StaffTab />}

      {/* Error / Success / Save — only for society + financial */}
      {innerTab !== "staff" && (
        <>
          {error && (
            <div style={{
              background: "#ff525215", border: "1px solid #ff525240", borderRadius: "10px",
              padding: "10px 14px", fontSize: "13px", color: "#ff5252", marginBottom: "12px",
            }}>⚠ {error}</div>
          )}
          {saved && (
            <div style={{
              background: "#4caf5015", border: "1px solid #4caf5040", borderRadius: "10px",
              padding: "10px 14px", fontSize: "13px", color: "#4caf50", marginBottom: "12px",
            }}>✅ Settings saved successfully.</div>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              width: "100%", background: "#6c63ff", color: "#fff",
              border: "none", borderRadius: "10px", padding: "13px",
              fontSize: "15px", fontWeight: "600",
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? "Saving…" : "Save Settings"}
          </button>
          <div style={{ fontSize: "11px", color: "#3a3a50", textAlign: "center", marginTop: "16px" }}>
            Only President can edit settings.
          </div>
        </>
      )}
    </div>
  );
}
